import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  CalendarClock,
  Clock,
  Compass,
  FileText,
  FolderOpen,
  GraduationCap,
  Grid2x2,
  LayoutDashboard,
  LifeBuoy,
  Inbox,
  Lightbulb,
  LineChart,
  ListTodo,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Mountain,
  Network,
  Plug,
  SearchCheck,
  UserCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AuthMeResponse } from '@/api/auth.api';
import type { Capability } from '@/api/capabilities.api';
import { hasCapability } from '@/lib/capabilities';
import type { SystemRole } from '@/types';

/**
 * The grouped sections the sidebar renders, in order. Each nav item declares
 * its `section`; the sidebar draws a labelled header per non-empty section.
 * Mirrors the Cmd-K command taxonomy so the two surfaces agree.
 */
export type NavSection = 'my-work' | 'team' | 'communicate' | 'knowledge' | 'customer' | 'analyze' | 'admin';

export interface NavSectionDef {
  id: NavSection;
  label: string;
  /** Admin & Settings collapses (default open) to tuck away the gated tools. */
  collapsible?: boolean;
}

export const NAV_SECTIONS: NavSectionDef[] = [
  { id: 'my-work', label: 'My Work' },
  { id: 'team', label: 'Team' },
  { id: 'communicate', label: 'Communicate' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'customer', label: 'Customer' },
  { id: 'analyze', label: 'Analyze' },
  { id: 'admin', label: 'Admin & Settings', collapsible: true },
];

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /** Which sidebar section this item belongs to. */
  section: NavSection;
  /**
   * Roles allowed to see this nav item. Undefined = visible to everyone.
   * Server still enforces RBAC on the actual route — this just hides it from
   * the chrome.
   */
  roles?: SystemRole[];
  /**
   * Capability required to see this nav item (ADR-017 semantics:
   * visionary/integrator pass implicitly). Composes with `roles` —
   * both must pass when both are set. Used for grant-based surfaces
   * like CS, where access isn't a function of role.
   */
  capability?: Capability;
  /** Surfaces in the mobile bottom-tab bar (max 5). */
  mobile?: boolean;
}

/**
 * Single source of truth for app navigation. Used by Sidebar (desktop,
 * grouped by `section`) and MobileBottomNav (the `mobile` flag). Array order
 * within a section is the display order; the mobile bar takes the first five
 * `mobile: true` items (My Day, To-Dos, Rocks, L10 Meetings, Training).
 */
export const NAV_ITEMS: NavItem[] = [
  // ── My Work — every employee's daily surfaces ─────────────────────────
  {
    id: 'dashboard',
    label: 'My Day',
    href: '/dashboard',
    icon: LayoutDashboard,
    section: 'my-work',
    mobile: true,
  },
  { id: 'notifications', label: 'Notifications', href: '/notifications', icon: Inbox, section: 'my-work' },
  { id: 'eos-todos', label: 'To-Dos', href: '/eos/todos', icon: ListTodo, section: 'my-work', mobile: true },
  { id: 'eos-rocks', label: 'Rocks', href: '/eos/rocks', icon: Mountain, section: 'my-work', mobile: true },
  { id: 'eos-issues', label: 'Issues', href: '/eos/issues', icon: Lightbulb, section: 'my-work' },
  {
    id: 'eos-one-on-ones',
    label: '1-on-1s',
    href: '/eos/one-on-ones',
    icon: MessagesSquare,
    section: 'my-work',
  },

  {
    id: 'eos-delegate-elevate',
    label: 'Delegate & Elevate',
    href: '/eos/delegate-elevate',
    icon: Grid2x2,
    section: 'my-work',
  },

  // ── Team — the shared operating system (EOS) ──────────────────────────
  {
    id: 'eos-meetings',
    label: 'L10 Meetings',
    href: '/eos/meetings',
    icon: CalendarClock,
    section: 'team',
    mobile: true,
  },
  { id: 'scorecards', label: 'Scorecards', href: '/scorecards', icon: BarChart3, section: 'team' },
  { id: 'eos-vto', label: 'V/TO', href: '/eos/vto', icon: Compass, section: 'team' },
  {
    id: 'eos-people',
    label: 'People Analyzer',
    href: '/eos/people',
    icon: UserCheck,
    section: 'team',
    roles: ['visionary', 'integrator', 'department_lead'],
  },
  { id: 'accountability', label: 'Org Chart', href: '/accountability', icon: Network, section: 'team' },
  { id: 'employees', label: 'Directory', href: '/employees', icon: Users, section: 'team' },

  // ── Communicate (Stream E). Sidebar-only (Kate 2026-07-20): no
  // mobile:true — the header MessageCircle pill is the mobile entry. ──
  {
    id: 'communication',
    label: 'Communication',
    href: '/communication',
    icon: Megaphone,
    section: 'communicate',
  },
  { id: 'messages', label: 'Messages', href: '/messages', icon: MessageCircle, section: 'communicate' },

  // ── Knowledge — training + controlled docs ────────────────────────────
  {
    id: 'training',
    label: 'Training',
    href: '/training',
    icon: GraduationCap,
    section: 'knowledge',
    mobile: true,
  },
  { id: 'sops', label: 'SOPs', href: '/compliance/sops', icon: FileText, section: 'knowledge' },
  // Document repository (ADR-035) — visible to everyone; the list is
  // access-filtered per the caller's role/seat grants, and only holders of
  // `manage_documents` see the upload/manage controls on the page.
  { id: 'documents', label: 'Documents', href: '/documents', icon: FolderOpen, section: 'knowledge' },

  // ── Customer — CRM + Customer Service. Grant-based, not role-based. ────
  {
    id: 'clients',
    label: 'Clients',
    href: '/clients',
    icon: Building2,
    section: 'customer',
    capability: 'use_clients',
  },
  {
    id: 'cs',
    label: 'Customer Service',
    href: '/cs',
    icon: LifeBuoy,
    section: 'customer',
    capability: 'use_cs',
  },
  {
    id: 'cs-operations',
    label: 'CS Operations',
    href: '/cs/operations',
    icon: Activity,
    section: 'customer',
    capability: 'view_cs_operations',
  },

  // ── Analyze — the industry-neutral reporting surface (ADR-033). Reads
  // the Metric/MetricEntry scorecard store; department leads get their
  // department roll-up. Org-gated fulfillment reports cross-link from it. ──
  {
    id: 'insights',
    label: 'Insights',
    href: '/insights',
    icon: LineChart,
    section: 'analyze',
    roles: ['visionary', 'integrator', 'department_lead'],
  },

  // ── Admin & Settings. Role-gated in the chrome; the server enforces the
  // real gate on every request. Home for the connect-other-systems +
  // configuration surfaces that previously had no sidebar presence. ──────
  {
    id: 'integrations',
    label: 'Integrations',
    href: '/settings/integrations',
    icon: Plug,
    section: 'admin',
    roles: ['visionary', 'integrator'],
  },
  {
    id: 'departments',
    label: 'Departments',
    href: '/settings/departments',
    icon: Building2,
    section: 'admin',
    roles: ['visionary', 'integrator'],
  },
  {
    id: 'training-admin',
    label: 'Training Admin',
    href: '/admin/training/courses',
    icon: GraduationCap,
    section: 'admin',
    roles: ['visionary', 'integrator'],
  },
  {
    id: 'overtime',
    label: 'Overtime',
    href: '/admin/overtime',
    icon: Clock,
    section: 'admin',
    roles: ['visionary', 'integrator'],
  },
  {
    id: 'alerts',
    label: 'Alerts',
    href: '/settings/alerts',
    icon: Bell,
    section: 'admin',
    roles: ['visionary', 'integrator'],
  },
  // Investigations settings (ADR-049) — grant-based, so a quality lead
  // holding `manage_investigations` sees it without being a superadmin.
  {
    id: 'investigations-admin',
    label: 'Investigations',
    href: '/settings/investigations',
    icon: SearchCheck,
    section: 'admin',
    capability: 'manage_investigations',
  },
];

/** Items visible to the authenticated user — role AND capability gates. */
export function navItemsFor(me: AuthMeResponse | null): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (item.roles && (!me || !item.roles.includes(me.membership.role))) return false;
    if (item.capability && (!me || !hasCapability(me, item.capability))) return false;
    return true;
  });
}

/** The visible items grouped into their sections, empty sections dropped —
 *  the shape the desktop sidebar renders. */
export function navSectionsFor(me: AuthMeResponse | null): { section: NavSectionDef; items: NavItem[] }[] {
  const visible = navItemsFor(me);
  return NAV_SECTIONS.map((section) => ({
    section,
    items: visible.filter((item) => item.section === section.id),
  })).filter((group) => group.items.length > 0);
}

export const MOBILE_NAV_ITEMS = NAV_ITEMS.filter((item) => item.mobile).slice(0, 5);
